import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { RESPONSE_CODE } from "../../api/network/constants";
import { handleAuthErrorCode } from "../../api/network/errorCodes";
import {
  useCreateCustomWidgetMutation,
  useEditCustomWidgetMutation,
} from "../../api/network/overviewApiServices";
import {
  useGetReportSpecificFieldsQuery,
  useRunCustomReportQuery,
} from "../../api/network/reportApiServices";
import { TModalsState, setModalsData } from "../../api/store/commonSlice";
import {
  DASHBOARD_WIDGET_TYPE,
  TCustomWidgetPayload,
} from "../../api/types/Overview";
import { TReportField } from "../../api/types/Report";
import svgIcons from "../../assets/svg/svgIcons";
import {
  AdminFormFieldCheckbox,
  AdminFormFieldDropdown,
  AdminFormFieldInput,
  AdminFormFieldSubmit,
} from "../../components/admin/formFields";
import FTModal from "../../components/common/FTModal";
import { APP_CONFIG, REPORT_TYPES } from "../../constants/constants";
import useConversion from "../../hooks/conversion";
import {
  AGGREGATE_OPERATOR,
  COLUMN_TYPE,
} from "../reports/components/popupReportColumnAggregation";
import { REPORT_FIELD_OUTPUT_TYPES } from "../reports/constants";
import WidgetChartAreaGenerator from "./components/widgetChartAreaGenerator";
import { useOverviewContext } from "./context/overview.context";
import { useChartUtils } from "./hooks/chartUtils";
import { EWidgetChartTypes } from "./types/overview.types";
import { getChartTypeIdByName, getChartTypeNameById } from "./utils/chart";

interface GraphState {
  graphTitle: string;
  calculationType: string | null;
  groupBy: string | null;
  subGroupBY: string | null;
  showAverageLine: boolean;
}

const ModalUseCustomReport = () => {
  const { t: tMain } = useTranslation();

  const { t } = useTranslation("translation", {
    keyPrefix: "reports.customReport",
  });
  const { t: tChart } = useTranslation("translation", {
    keyPrefix: "overviewScreen.switch_chart_type",
  });
  const dispatch = useDispatch();
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const customWidgetToEdit = modalsState.customWidgetToEdit;
  const customReport = modalsState.customWidgetReport;

  const [selectedChartType, setSelectedChartType] = useState<EWidgetChartTypes>(
    getChartTypeNameById(customWidgetToEdit?.graph_type || 0) ||
      EWidgetChartTypes.PIE
  );
  const {
    allTimeFilters,
    timeFilter,
    allGroupsSelected,
    selectedDataType,
    selectedGroups,
  } = useOverviewContext();

  const [createWidgetAPITrigger, { isLoading: isCreating }] =
    useCreateCustomWidgetMutation();
  const [editWidgetAPITrigger, { isLoading: isUpdating }] =
    useEditCustomWidgetMutation();
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [previewDisabled, setPreviewDisabled] = useState<boolean>(true);

  const [graphState, setGraphState] = useState<GraphState>({
    graphTitle: customWidgetToEdit?.title || "",
    calculationType: customWidgetToEdit?.y_axis || null,
    groupBy: customWidgetToEdit?.x_axis || null,
    subGroupBY: customWidgetToEdit?.group_by || null,
    showAverageLine: customWidgetToEdit?.show_average || false,
  });

  const {
    data: dataSpecificReport,
    isFetching: isFetchingSpecificReport,
    isError: isSpecificReportError,
    error: specificReportError,
    isUninitialized: isUninitializedSpecificReport,
    refetch: refetchDataSpecificReport,
  } = useGetReportSpecificFieldsQuery(
    {
      type: REPORT_TYPES.CUSTOM,
      report_id: customReport?.id ?? 0,
    },
    {
      skip: !customReport,
    }
  );

  // replica of getTimeRange in `useGroupsAndAssetsUtils` under `overviewScreenUtils.ts`
  const getTimeRange = (timeFilter: any) => {
    const filter = allTimeFilters.find((filter) => filter.slug === timeFilter);
    if (!filter)
      return {
        start_time: undefined,
        end_time: undefined,
        ds_time_range: undefined,
      };

    const [start, end] = filter.timeRange || [];
    return {
      start_time: start ? start : undefined,
      end_time: end ? end : undefined,
      ds_time_range: filter.value,
    };
  };

  // 8/25/2025 - for preview we are using start and end times from the report definition
  const specificReportStartTime = dataSpecificReport?.report_filters?.find(
    (filter) => filter.param === "start_time"
  )?.value;
  const specificReportEndTime = dataSpecificReport?.report_filters?.find(
    (filter) => filter.param === "end_time"
  )?.value;

  const {
    data: dataReport,
    isFetching,
    isError,
    error: reportError,
  } = useRunCustomReportQuery(
    {
      id: customReport?.id?.toString() ?? "",
      fields: dataSpecificReport?.report_fields
        ?.map((field) => {
          return field.id;
        })
        .join(","),
      page: 1,
      page_size: 10,
      report_fields: dataSpecificReport?.report_fields?.map(
        (field: TReportField) => {
          const groupByIsActive = field.identifier === graphState.groupBy;
          return {
            field: parseInt(field.field.all_field_id ?? "0"),

            // aggregation param
            ...(!!field.aggregate_function
              ? {
                  aggregate_function: field.aggregate_function,
                  column_type: field.column_type,
                  value: field.value,
                  operator: field.operator,
                }
              : {}),
            // group by param
            ...(groupByIsActive
              ? {
                  is_group_by: field.identifier === graphState.groupBy,
                  group_order: field.group_order,
                }
              : {}),
          };
        }
      ),
      // device groups param
      // device_groups: "",
      deviceid: "",

      // for showing average line
      ...(graphState.showAverageLine && !!graphState.calculationType
        ? {
            custom_widget_id: !!customWidgetToEdit ? customWidgetToEdit.id : -1,
            calculate_average_using: graphState.calculationType,
          }
        : {}),

      // time range param
      // ...getTimeRange(timeFilter),
      // 8/25/2025 - for preview we are using start and end times from the report definition
      start_time: !!specificReportStartTime
        ? parseInt(`${specificReportStartTime}`)
        : undefined,
      end_time: !!specificReportEndTime
        ? parseInt(`${specificReportEndTime}`)
        : undefined,
      // order by param
      ordering: `-${
        dataSpecificReport?.report_fields?.find(
          (field) => field.identifier === `${graphState.calculationType}`
        )?.field?.id
      }`,
      // start_time: !!allTimeFilters.find((filter) => filter.slug === timeFilter)
      //   ?.timeRange[0]
      //   ? jsTimeToDBTime(
      //       allTimeFilters
      //         .find((filter) => filter.slug === timeFilter)
      //         ?.timeRange[0]?.getTime() ?? 0
      //     )
      //   : undefined,
      // end_time: !!allTimeFilters.find((filter) => filter.slug === timeFilter)
      //   ?.timeRange[1]
      //   ? jsTimeToDBTime(
      //       allTimeFilters
      //         .find((filter) => filter.slug === timeFilter)
      //         ?.timeRange[1]?.getTime() ?? 0
      //     )
      //   : undefined,
      // device_groups: "codes",
      // deviceid: "",

      // ds_time_range: allTimeFilters.find((filter) => filter.slug === timeFilter)
      //   ?.value,
    },
    {
      skip:
        isUninitializedSpecificReport ||
        isFetchingSpecificReport ||
        !graphState.calculationType ||
        !graphState.groupBy ||
        !customReport?.id?.toString(),
      refetchOnMountOrArgChange: true,
    }
  );
  // show a modal if API returns an error
  useEffect(() => {
    if (isError) {
      if (
        (reportError as FetchBaseQueryError)?.status ===
        RESPONSE_CODE.UNAUTHORIZED
      ) {
        // handle auth error
        handleAuthErrorCode(reportError as FetchBaseQueryError);
      } else {
        // other errors
        toast.error("An error occurred while fetching data");
      }
    }
  }, [isError]);

  // instead of a useMemo, we will leverage `checkAndFetchDrillDownData` to prepare `computedChartData`
  // const computedChartData = useMemo(() => {
  //   if (
  //     !!dataReport &&
  //     !!dataReport?.results &&
  //     !!dataSpecificReport?.report_fields &&
  //     !!dataReport?.results?.[0] &&
  //     !!graphState.groupBy
  //   ) {
  //     return dataReport && dataReport?.results && graphState.groupBy
  //       ? [...(dataReport?.results || [])]?.sort(
  //           (a, b) => a[graphState.groupBy ?? ""] - b[graphState.groupBy ?? ""]
  //         )
  //       : [];
  //   } else {
  //     return [];
  //   }
  // }, [dataReport, dataSpecificReport, graphState]);

  const [computedChartData, setComputedChartData] = useState<any[]>([]);
  const [chartGroupingLabels, setChartGroupingLabels] = useState<string[]>([]);
  const [fetchingWidgetData, setFetchingWidgetData] = useState(false);
  const { checkAndFetchDrillDownData } = useChartUtils();
  const { convertToUnit } = useConversion();
  useEffect(() => {
    if (
      !!dataReport &&
      !!dataReport?.results &&
      !!dataSpecificReport?.report_fields &&
      !!dataReport?.results?.[0] &&
      !!graphState.groupBy &&
      !!graphState.calculationType &&
      !isFetching
    ) {
      if (!!graphState?.graphTitle) {
        setPreviewDisabled(false);
      }

      const initialChartData = [...(dataReport?.results || [])]
        ?.sort(
          (a, b) =>
            a[graphState.calculationType ?? ""] -
            b[graphState.calculationType ?? ""]
        )
        // carry out data unit conversion as applicable
        .map((resultItem) => {
          const fieldData = dataSpecificReport?.report_fields?.find(
            (field) => field.identifier === `${graphState.calculationType}`
          );
          return {
            [`${graphState.groupBy}`]: resultItem[`${graphState.groupBy}`],
            // [graphYAxisDataKey]: resultItem[graphYAxisDataKey],
            [`${graphState.calculationType}`]: parseFloat(
              convertToUnit(
                fieldData?.output_type || REPORT_FIELD_OUTPUT_TYPES.TEXT,
                resultItem?.[`${graphState.calculationType}`] || "",
                fieldData?.convert_to_whole_number
              )?.finalData || "0"
            ),
          };
        });

      if (!!graphState?.subGroupBY) {
        // if subGroupBy is present, fetch the drilldown data
        setFetchingWidgetData(true);
        checkAndFetchDrillDownData({
          retrievedComputedChartData: initialChartData,
          dataSpecificReport,
          dataReport,
          isFetching,
          setChartGroupingLabels,
          setComputedChartData,
          setFetchingWidgetData,
          reportId: customReport?.id,
          widget: {
            x_axis: graphState.groupBy,
            y_axis: graphState.calculationType,
            group_by: graphState.subGroupBY ?? "",
            show_average: graphState.showAverageLine,
          },
          graphXAxisDataKey: graphState.groupBy ?? "",
          graphYAxisDataKey: graphState.calculationType ?? "",
          fetchCompleteCallback: () => {
            setFetchingWidgetData(false);
          },
        });
      } else {
        // if no subGroupBy, just set the initialChartData
        setComputedChartData(initialChartData);
        setFetchingWidgetData(false);
      }
    } else {
      setPreviewDisabled(true);
    }
  }, [dataReport, isFetching, dataSpecificReport, graphState.subGroupBY]);

  useEffect(() => {
    if (
      !customReport ||
      !graphState.groupBy ||
      !graphState.calculationType ||
      graphState.graphTitle?.trim()?.length === 0
    ) {
      setPreviewDisabled(true);
    } else {
      setPreviewDisabled(false);
    }
  }, [customReport, graphState]);

  const eligibleChartVariables: TReportField[] = useMemo(
    () =>
      dataSpecificReport?.report_fields?.filter((field) => field.is_group_by) ??
      [],
    [dataSpecificReport]
  );

  // Get aggregate function name from the report fields
  const aggregateFunctionFields = dataSpecificReport?.report_fields?.filter(
    (field) =>
      field.aggregate_function !== "" &&
      (field.column_type === COLUMN_TYPE.AGGREGATE ||
        field.column_type === COLUMN_TYPE.AGGREGATE_CUSTOM)
  );

  // const dispatch = useDispatch();

  const handleChartChange = (newChartType: EWidgetChartTypes) => {
    setSelectedChartType(newChartType);
    if (
      newChartType === EWidgetChartTypes.PIE ||
      newChartType === EWidgetChartTypes.DOUGHNUT
    ) {
      setGraphState({
        ...graphState,
        showAverageLine: false,
      });
    }
  };

  const hideModal = () => {
    setGraphState({
      graphTitle: "",
      calculationType: null,
      groupBy: null,
      subGroupBY: null,
      showAverageLine: false,
    });
    dispatch(
      setModalsData({
        ...modalsState,
        customWidgetReport: null,
        customWidgetToEdit: null,
        showUseCustomReport: false,
      })
    );
  };

  const onTitleChange = (e: any) => {
    setGraphState((state: GraphState) => ({
      ...state,
      graphTitle: e.target.value,
    }));
    setShowPreview(false);
  };

  const onCalculationTypeChange = (type: string | null) => {
    // setCalculationType(type);
    setGraphState((state: GraphState) => ({ ...state, calculationType: type }));
    setShowPreview(false);
  };

  const onGroupByChange = (type: string | null) => {
    // setGroupBy(type);
    setGraphState((state: GraphState) => ({ ...state, groupBy: type }));
    setShowPreview(false);
  };

  const onSubGroupByChange = (type: string | null) => {
    // setSubGroupBy(type);
    setGraphState((state: GraphState) => ({ ...state, subGroupBY: type }));
    setShowPreview(false);
  };

  const onShowAverageLineChange = (show: boolean) => {
    setGraphState({
      ...graphState,
      showAverageLine: show,
    });
    setShowPreview(false);
  };

  const onPreviewClick = () => {
    setShowPreview(true);
  };

  const legendLabelFormatter = (value: string) => {
    if (
      selectedChartType === EWidgetChartTypes.PIE ||
      selectedChartType === EWidgetChartTypes.DOUGHNUT
    ) {
      return Math.round(+value).toString();
    } else {
      return value;
    }
  };

  const onCreateUpdateClick = () => {
    if (
      !customReport ||
      !graphState.groupBy ||
      !graphState.calculationType ||
      graphState.graphTitle?.trim()?.length === 0
    )
      return;
    const widgetPayload = {
      report: customReport.id,
      title: graphState.graphTitle ?? "",
      x_axis: graphState.groupBy ?? "",
      y_axis: graphState.calculationType ?? "",
      group_by: graphState.subGroupBY ?? "",
      graph_type: getChartTypeIdByName(selectedChartType) ?? 1,
      show_average: graphState.showAverageLine,
    } as TCustomWidgetPayload;
    (!!customWidgetToEdit
      ? editWidgetAPITrigger({
          widget_id: customWidgetToEdit.id,
          ...widgetPayload,
        })
      : createWidgetAPITrigger(widgetPayload)
    )
      .then((res) => {
        toast.success(
          !!customWidgetToEdit
            ? "Widget updated successfully."
            : "Widget created successfully. Use the Add/Remove Widgets option to add the widget to the dashboard of your choice."
        );
        hideModal();
      })
      .catch((err) => {
        toast.error(
          !!customWidgetToEdit
            ? "Error updating widget."
            : "Error creating widget."
        );
      });
  };

  const selectedGroup = useMemo(() => {
    return eligibleChartVariables.find(
      (group) => group.identifier === graphState.groupBy
    );
  }, [graphState.groupBy, eligibleChartVariables]);

  // check the current status of custom report to ensure that group by, sub group by and aggregation are set
  const customReportGroupByIdentifier =
    dataSpecificReport?.report_fields?.find(
      (field) => !!field.is_group_by && field.group_order === 1
    )?.identifier || "";
  const customReportSubGroupByIdentifier =
    dataSpecificReport?.report_fields?.find(
      (field) => !!field.is_group_by && field.group_order === 2
    )?.identifier || "";
  const customReportAggregateFunctionIdentifiers =
    dataSpecificReport?.report_fields
      ?.filter((field) => !!field.aggregate_function)
      ?.map((field) => field.identifier) || [];
  const customReportSupportsWidgets =
    !!customReportGroupByIdentifier &&
    // !!customReportSubGroupByIdentifier && // disabled to allow display of chart even if grouping is unavailable
    customReportAggregateFunctionIdentifiers.length > 0;
  const customReportAlignsWithWidgetSettings =
    customReportGroupByIdentifier === graphState.groupBy &&
    // customReportSubGroupByIdentifier === graphState.subGroupBY && // disabled to allow display of chart even if grouping is unavailable
    !!graphState.calculationType &&
    customReportAggregateFunctionIdentifiers.includes(
      graphState.calculationType
    );

  if (!modalsState.showUseCustomReport) return null;

  return (
    <FTModal
      visible={true}
      dismissible={true}
      heading=""
      onHide={hideModal}
      containerStyle="w-[calc(100vw-20%)] overflow-hidden flex flex-col"
      position="center"
    >
      <div className="Xmax-h-[calc(100vh-15vh)] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between">
          <div className="text-3xl font-bold">
            {/* tl */}
            {t(
              !!customWidgetToEdit
                ? "use_custom_report_edit"
                : "use_custom_report"
            )}
          </div>
          <div className="flex gap-4">
            {(isFetching || isFetchingSpecificReport || fetchingWidgetData) && (
              <span className="flex items-center justify-center min-w-8">
                <img
                  src={svgIcons.LoadingAnimation}
                  alt="loading-animation"
                  className="size-8"
                />
              </span>
            )}
            <AdminFormFieldSubmit
              type="button"
              variant="danger-alt"
              customWrapperClass=""
              //  tl
              label={t("cancel")}
              titleText={t("cancel")}
              onClick={hideModal}
              // disabled={disabled || currentAllList.length === 0}
            />
            <span className="relative">
              {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
                <pre className="absolute top-full left-0 text-[8px]">
                  previewDisabled: {previewDisabled.toString()}
                  <br />
                  isFetching: {isFetching.toString()}
                  <br />
                  fetchingWidgetData: {fetchingWidgetData.toString()}
                  <br />
                  isFetchingSpecificReport:{" "}
                  {isFetchingSpecificReport.toString()}
                  <br />
                  computedChartData: {computedChartData.length || 0}
                  <br />
                  customReportSupportsW...:{" "}
                  {JSON.stringify(customReportSupportsWidgets)}
                  <br />
                  customReportAlignsWith...:{" "}
                  {JSON.stringify(customReportAlignsWithWidgetSettings)}
                  <br />
                  {JSON.stringify(
                    customReportAggregateFunctionIdentifiers,
                    null,
                    2
                  )}
                </pre>
              )}
              <AdminFormFieldSubmit
                type="button"
                variant="secondary"
                //  tl
                label={t("preview")}
                titleText={t("preview")}
                onClick={onPreviewClick}
                disabled={
                  previewDisabled ||
                  isFetching ||
                  fetchingWidgetData ||
                  isFetchingSpecificReport ||
                  computedChartData.length === 0 ||
                  !customReportSupportsWidgets ||
                  !customReportAlignsWithWidgetSettings
                }
              />
            </span>
            <AdminFormFieldSubmit
              type="button"
              variant="primary"
              //  tl
              label={t(!!customWidgetToEdit ? "publish_edit" : "publish")}
              titleText={t("publish")}
              onClick={onCreateUpdateClick}
              disabled={
                !showPreview ||
                isCreating ||
                isUpdating ||
                !customReportSupportsWidgets ||
                !customReportAlignsWithWidgetSettings
              }
            />
          </div>
        </div>

        {/* Headings */}
        <div className="text-2xl mt-2 font-bold text-accent-blue-dark">
          {customReport?.name ?? "Custom Report"}
        </div>
        <div className="text-xl mt-6 font-bold">{tChart("heading")}</div>

        {/* Chart Type Selection */}
        <div className="Xflex grid grid-cols-6 my-5">
          {Object.values(EWidgetChartTypes).map((chartType, i) => (
            <AdminFormFieldSubmit
              key={`${chartType}_${i}`}
              label={
                <span className="flex items-center justify-start w-full">
                  <img
                    src={svgIcons[`ChartType${chartType}`]}
                    alt={chartType}
                    className={`size-6 mr-2 ${selectedChartType === chartType ? "group-focus:grayscale-0 group-focus:brightness-0 group-focus:invert group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert" : ""}`}
                  />
                  <span className="flex-grow overflow-hidden whitespace-nowrap text-ellipsis">
                    {tChart(`types.${chartType}`)}
                  </span>
                </span>
              }
              type="button"
              variant={
                selectedChartType === chartType ? "primary" : "primary-alt"
              }
              buttonDimensionClass={`h-11 w-full px-4${selectedChartType === chartType ? " pointer-events-none touch-none" : ""}`}
              onClick={() => handleChartChange(chartType)}
              customWrapperClass={`Xflex-1 mx-2`}
              titleText={tChart(`types.${chartType}`)}
            />
          ))}
        </div>

        {/* Main Content (3 Columns) */}
        <div className="flex overflow-hidden [&>div]:border-r [&>div]:border-gray-300 last:[&>div]:border-0 [&>div]:px-6">
          {/* Variables */}
          <div className="flex-[2] overflow-hidden">
            <div className="text-xl font-bold mt-2">{t("variables")}</div>
            <div className="overflow-auto mt-4 h-4/5">
              {eligibleChartVariables?.map((field) => (
                <div
                  key={field.id}
                  className="flex items-center justify-between pb-2"
                >
                  {field.field.name}
                </div>
              ))}
            </div>
          </div>

          {/* Chart Options Selection */}
          <div className="flex-[3]">
            <div className="text-xl font-bold mt-2">{t("details")}</div>
            <div className="overflow-auto mt-4 min-h-4/5">
              <AdminFormFieldInput
                label={t("details_section.graph_title")}
                bottomLabel={t("details_section.min_char")}
                type="text"
                id="title"
                name="title"
                value={graphState.graphTitle}
                onChange={(e) => onTitleChange(e)}
              />
              <AdminFormFieldDropdown
                label={t("details_section.calculation")}
                id="calculation"
                name="calculation"
                placeholder={t("details_section.select")}
                customWrapperClass="mt-3"
                onChange={(e) => onCalculationTypeChange(e?.value ?? null)}
                // onBlur={handleBlur}
                value={graphState.calculationType}
                options={aggregateFunctionFields?.map((option) => ({
                  value: option.identifier,
                  label: `${option.name} (${option.aggregate_function}${option.operator === AGGREGATE_OPERATOR.MULTIPLY ? `*${option.value}` : ""})`,
                }))}
                // touched={touched.acl_role}
                // error={errors.acl_role}
                disabled={
                  isFetchingSpecificReport || isFetching || fetchingWidgetData
                }
              />
              <AdminFormFieldDropdown
                label={t("details_section.group_by")}
                id="group_by"
                name="group_by"
                placeholder={t("details_section.select")}
                customWrapperClass="mt-3"
                onChange={(e) => {
                  onGroupByChange(e?.value ?? null);
                }}
                // onBlur={handleBlur}
                value={graphState.groupBy}
                options={eligibleChartVariables
                  ?.filter((field) => field.group_order === 1)
                  ?.map((option) => ({
                    value: option.identifier,
                    label: option.name,
                  }))}
                menuPlacement="top"
                disabled={
                  isFetchingSpecificReport || isFetching || fetchingWidgetData
                }
              />

              <AdminFormFieldDropdown
                label={t("details_section.sub_group_by")}
                id="sub_group_by"
                name="sub_group_by"
                disabled={
                  selectedChartType === EWidgetChartTypes.PIE ||
                  selectedChartType === EWidgetChartTypes.DOUGHNUT ||
                  isFetchingSpecificReport ||
                  isFetching ||
                  fetchingWidgetData
                }
                placeholder={t("details_section.sub_group_by")}
                customWrapperClass="mt-3"
                onChange={(e) => onSubGroupByChange(e?.value ?? null)}
                // onBlur={handleBlur}
                value={graphState.subGroupBY}
                options={eligibleChartVariables
                  ?.filter((field) => field.group_order > 1)
                  ?.map((option) => ({
                    value: option.identifier,
                    label: option.name,
                  }))}
                menuPlacement="top"
                isClearable={true}
              />

              {!!graphState.calculationType &&
                ![EWidgetChartTypes.PIE, EWidgetChartTypes.DOUGHNUT].includes(
                  selectedChartType
                ) && (
                  <AdminFormFieldCheckbox
                    label="Show Average Line"
                    id="show_average_line"
                    type="checkbox"
                    name="show_average_line"
                    checked={graphState.showAverageLine}
                    onChange={() =>
                      onShowAverageLineChange(!graphState.showAverageLine)
                    }
                    customWrapperClass="mt-4"
                    disabled={
                      selectedChartType === EWidgetChartTypes.PIE ||
                      selectedChartType === EWidgetChartTypes.DOUGHNUT ||
                      isFetchingSpecificReport ||
                      isFetching ||
                      fetchingWidgetData
                    }
                  />
                )}
            </div>
          </div>

          {/* Preview */}
          <div className="flex-[4] flex flex-col">
            <div className="text-xl font-bold mt-2 text-left">
              {t("preview")}
            </div>
            <div className="flex flex-col flex-1 p-6 pr-0">
              {!!customWidgetToEdit &&
              (!customReportSupportsWidgets ||
                !customReportAlignsWithWidgetSettings) ? (
                <>
                  <div className="flex-1 flex justify-center items-center shadow-lg text-sm text-center text-gray-400">
                    <p className="text-sm text-center text-gray-400">
                      Current report configuration does not align with the
                      saved/expected widget options.
                      <br />
                      {customReportSupportsWidgets
                        ? "To fix this, please edit the widget."
                        : "To fix this, please edit the report."}
                    </p>
                  </div>
                </>
              ) : !dataReport ||
                dataReport?.results?.length === 0 ||
                !showPreview ? (
                <>
                  <div className="flex-1 flex justify-center items-center shadow-lg text-sm text-center text-gray-400">
                    {(!previewDisabled && computedChartData.length > 0) ||
                    isFetching ? (
                      <>
                        {fetchingWidgetData ||
                        isFetching ||
                        isFetchingSpecificReport ? (
                          <>
                            <img
                              src={svgIcons.LoadingAnimation}
                              alt="loading-animation"
                              className="size-8"
                            />
                          </>
                        ) : (
                          <>
                            {`${t("preview_ready_line1")}`}
                            <br />
                            {`${t("preview_ready_line2")}`}
                          </>
                        )}
                      </>
                    ) : (
                      t("fill_details")
                    )}
                  </div>
                </>
              ) : (
                <div className="flex-[4] flex flex-col">
                  <div className="font-bold mb-4 text-left">
                    {graphState.graphTitle}
                  </div>
                  {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
                    <pre className="absolute h-full top-0 left-0 overflow-auto text-[8px] bg-gray-200 opacity-40 pointer-events-none z-50">
                      {/* customList: {JSON.stringify(customList, null, 2)}
                                    <br /> */}
                      graphXAxisDataKey: {graphState.groupBy ?? ""}
                      <br />
                      graphYAxisDataKey: {graphState.calculationType ?? ""}
                      <br />
                      group_by (grouping): {graphState.subGroupBY ?? ""}
                      <br />
                      groupingLabels: {chartGroupingLabels.join(", ")}
                      <br />
                      computedChartData:
                      {JSON.stringify(computedChartData, null, 2)}
                    </pre>
                  )}
                  <WidgetChartAreaGenerator
                    barChartMargin={
                      selectedChartType ===
                      EWidgetChartTypes.HORIZONTAL_BAR_GRAPH
                        ? { top: 0, right: 10, bottom: 0, left: -10 }
                        : { top: 0, right: 0, left: -35, bottom: 0 }
                    }
                    cellItemsForSectionColor={computedChartData}
                    chartGroupingLabels={
                      !!graphState.subGroupBY ? chartGroupingLabels : []
                    }
                    chartHighlightLabel={`Total ${selectedGroup?.name}`}
                    chartHighlightValue={dataReport?.count?.toString() ?? "0"}
                    chartType={selectedChartType}
                    chartWrapperHeightClass={
                      selectedChartType ===
                      EWidgetChartTypes.HORIZONTAL_BAR_GRAPH
                        ? "pt-[45%]"
                        : "pt-[45%]"
                    }
                    computedChartData={computedChartData}
                    graphXAxisDataKey={graphState.groupBy ?? ""}
                    graphYAxisDataKey={graphState.calculationType ?? ""}
                    legendHeading={selectedGroup?.name}
                    legendVerticalAlignClass="justify-start"
                    legendLabelFormatter={(value: any) => value}
                    legendValueFormatter={(value: any) =>
                      (isNaN(value) ? 0 : value)?.toFixed(2)?.replace(".00", "")
                    }
                    showChartTypeSwitchButton={false}
                    t={(value: any) => value}
                    widgetType={DASHBOARD_WIDGET_TYPE.CUSTOM}
                    xAxisTickLabelWidth={
                      selectedChartType ===
                      EWidgetChartTypes.HORIZONTAL_BAR_GRAPH
                        ? 40
                        : 80
                    }
                    // show average line
                    {...(graphState.showAverageLine &&
                    typeof dataReport?.results?.[0]?.avg_value === "number"
                      ? {
                          averageReferenceLineValue:
                            dataReport?.results?.[0]?.avg_value,

                          showAverageReferenceLineWithLabel: tMain(
                            "overviewScreen.charts.average_label"
                          ),
                        }
                      : {})}
                  />
                  <p className="text-gray-400 italic text-xs border-t border-t-slate-300 mt-3 pt-3">
                    Please note that the chart displayed above uses date range
                    selected for the report. However, the actual widget on the
                    dashboard will reflect data based on the dashboard date
                    filters and may vary from the above representation.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </FTModal>
  );
};
export default ModalUseCustomReport;
